import type { SubmitCallbackArgs } from '@inkeep/cxkit-types';
import * as Sentry from '@sentry/react';

type SupportTicketFormValues = {
  fullname: string;
  email: string;
  subject: string;
  description: string;
  hs_file_upload?: FileList;
  include_transcript?: boolean;
};

/** HubSpot text fields have a max length of 65,535 characters (2^16 - 1) */
const HUBSPOT_MAX_TEXT_LENGTH = 65_535;
const MAX_TRANSCRIPT_LENGTH = 50_000;

export const serializeTranscript = (conversation: SubmitCallbackArgs['conversation']): string => {
  if (!conversation?.messages?.length) {
    return '';
  }

  return conversation.messages
    .filter((msg) => msg.role !== 'system')
    .map((msg) => {
      const role = msg.role === 'user' ? 'User' : 'Truv AI';
      const content = typeof msg.content === 'string' ? msg.content : (msg.content?.map((c) => c.text).join('') ?? '');
      return `[${role}]: ${content}`;
    })
    .join('\n');
};

export const submitHubSpotTicket = async ({ values, conversation }: SubmitCallbackArgs): Promise<void> => {
  const PORTAL_ID = import.meta.env.VITE_APP_HUBSPOT_PORTAL_ID;
  const FORM_GUID = import.meta.env.VITE_APP_HUBSPOT_FORM_GUID;

  if (!(PORTAL_ID && FORM_GUID)) {
    throw new Error('HubSpot configuration missing');
  }

  const {
    fullname = '',
    email = '',
    subject = '',
    description: rawDescription = '',
    hs_file_upload,
    include_transcript,
  } = values as SupportTicketFormValues;

  let description = String(rawDescription);

  if (include_transcript && conversation) {
    const transcript = serializeTranscript(conversation);
    if (transcript) {
      const truncated =
        transcript.length > MAX_TRANSCRIPT_LENGTH
          ? `${transcript.slice(0, MAX_TRANSCRIPT_LENGTH)}\n[Transcript truncated]`
          : transcript;
      description += `\n\n--- Chat Transcript ---\n${truncated}\n--- End Transcript ---`;
    }
  }

  if (description.length > HUBSPOT_MAX_TEXT_LENGTH) {
    const suffix = '\n[Content truncated]';
    description = `${description.slice(0, HUBSPOT_MAX_TEXT_LENGTH - suffix.length)}${suffix}`;
  }

  const fullName = fullname.trim();
  const spaceIndex = fullName.indexOf(' ');
  const firstName = spaceIndex > -1 ? fullName.slice(0, spaceIndex) : fullName;
  const lastName = spaceIndex > -1 ? fullName.slice(spaceIndex + 1).trim() : '-';

  const formData = new FormData();
  formData.append('email', email);
  formData.append('firstname', firstName);
  formData.append('lastname', lastName);
  formData.append('TICKET.subject', subject);
  formData.append('TICKET.content', description);
  formData.append('TICKET.issue_type_main', 'Dashboard');

  const file = hs_file_upload?.[0];
  if (file) {
    formData.append('TICKET.hs_file_upload', file, file.name);
  }

  formData.append(
    'hs_context',
    JSON.stringify({
      pageUri: window.location.href,
      pageName: 'Dashboard',
    }),
  );

  const userFacingError = new Error('Unable to submit ticket. Please try again.');

  let response: Response;
  try {
    response = await fetch(`https://forms.hubspot.com/uploads/form/v2/${PORTAL_ID}/${FORM_GUID}`, {
      method: 'POST',
      body: formData,
    });
  } catch (error) {
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)));
    throw userFacingError;
  }

  if (!response.ok) {
    const errorBody = await response.text().catch(() => 'Unknown error');
    Sentry.captureException(new Error(`HubSpot submission failed: ${response.status} - ${errorBody}`));
    throw userFacingError;
  }
};
